import React from 'react';
import { Heart, MapPin } from 'lucide-react';

const HeritageStory: React.FC = () => {
  return (
    <section className="bg-paper-bright border-t border-b border-ink-100 py-24 lg:py-32 relative overflow-hidden">

      <div className="max-w-editorial mx-auto px-6 lg:px-10 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-16 items-center">

          {/* Text */}
          <div className="md:col-span-6">
            <p className="font-mono text-[11px] tracking-micro uppercase text-ember mb-6">
              — Our heritage
            </p>
            <h2 className="font-editorial italic font-light text-4xl lg:text-5xl text-ink-950 mb-10 leading-[1.1] tracking-tight">
              From Jamaica to the Great White North.
            </h2>

            <div className="space-y-5 max-w-prose">
              <p className="text-ink-700 text-lg leading-[1.65]">
                The Machete Man's story begins in <span className="text-ink-950 font-medium">Jamaica</span>,
                where the machete isn't just a tool — it's a third hand. Growing up on the island meant the
                blade was part of everyday life: chopping green coconuts, clearing the path to the river,
                maintaining the land. It represents self-reliance and provision.
              </p>
              <p className="text-ink-700 text-lg leading-[1.65]">
                Upon arriving in <span className="text-ink-950 font-medium">Canada</span>, he noticed a void.
                The Caribbean diaspora here understood the value of a good blade, but the broader
                Canadian market was missing out on this essential utility. Garden centers sold
                flimsy stamped metal that couldn't handle real work.
              </p>
              <p className="text-ink-700 text-lg leading-[1.65]">
                <span className="text-ink-950 font-medium">The Machete Man</span> emerged to bridge that gap.
                Based in Canada, he stands as the guardian of authentic quality for campers, cottagers, and
                gardeners. We bring the high-carbon tradition of the islands to a new generation of users
                across Canada, while keeping our promise to the community that knows us best.
              </p>
            </div>

            <div className="mt-12 flex items-center gap-12 border-t border-ink-200 pt-8">
              <div className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-ember" strokeWidth={1.5} />
                <div>
                  <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500">Homebase</p>
                  <p className="font-editorial italic text-lg text-ink-950">Canada</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Heart className="w-4 h-4 text-ember" strokeWidth={1.5} />
                <div>
                  <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500">Heritage</p>
                  <p className="font-editorial italic text-lg text-ink-950">Jamaica</p>
                </div>
              </div>
            </div>
          </div>

          {/* Image */}
          <div className="md:col-span-6 relative">
            <div className="aspect-[4/5] w-full overflow-hidden border border-ink-200 relative group">
              <img
                src="/images/heritage.webp"
                alt="Caribbean coast meets Canadian forest"
                className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 ease-editorial"
                style={{ objectPosition: 'center' }}
              />

              {/* Caption card */}
              <div className="absolute bottom-6 left-6 right-6 sm:right-auto sm:max-w-xs bg-paper-bright/95 backdrop-blur p-5 border-l-2 border-ember">
                <p className="font-editorial italic text-lg text-ink-950 leading-tight">
                  Bridging cultures.
                </p>
                <p className="text-xs text-ink-700 mt-2">
                  Introducing island utility to the northern backyard.
                </p>
              </div>
            </div>
          </div>

        </div>

        {/* Incolma introduction */}
        <div className="mt-24 lg:mt-32 pt-20 lg:pt-24 border-t border-ink-200">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-16 items-center">

            {/* Facts panel */}
            <div className="md:col-span-5 order-2 md:order-1">
              <div className="bg-ink-950 p-8 lg:p-10">
                <img
                  src="/images/incolma-logo.webp"
                  alt="Incolma — Manizales, Colombia"
                  className="h-12 w-auto mb-8"
                />
                <dl className="border-t border-paper-warm/15">
                  {[
                    ['Forge', 'Manizales, Colombia'],
                    ['Est.', 'May 20, 1960'],
                    ['Heritage', 'Gebr. Weyersberg, Solingen · 1787'],
                    ['Steel', 'Drop-forged high-carbon, 52–58 HRC'],
                    ['Construction', 'Full tang, every blade'],
                    ['Scale', "World's largest machete factory"],
                    ['Reach', '55+ countries, five continents'],
                    ['In Canada', 'Exclusively via The Machete Man'],
                  ].map(([term, def]) => (
                    <div key={term} className="flex justify-between gap-6 py-3.5 border-b border-paper-warm/15">
                      <dt className="font-mono text-[10px] tracking-micro uppercase text-paper-warm/50 pt-0.5 flex-shrink-0">{term}</dt>
                      <dd className="text-sm text-paper-warm text-right">{def}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>

            {/* Text */}
            <div className="md:col-span-7 order-1 md:order-2">
              <p className="font-mono text-[11px] tracking-micro uppercase text-ember mb-6">
                — Meet Incolma
              </p>
              <h2 className="font-editorial italic font-light text-4xl lg:text-5xl text-ink-950 mb-10 leading-[1.1] tracking-tight">
                The forge behind the blades.
              </h2>
              <div className="space-y-5 max-w-prose">
                <p className="text-ink-700 text-lg leading-[1.65]">
                  Every blade we sell is forged by <span className="text-ink-950 font-medium">Incolma</span> in
                  Manizales, Colombia, high in the coffee highlands. Its bladesmithing lineage runs back to{' '}
                  <span className="text-ink-950 font-medium">Gebr. Weyersberg of Solingen, Germany</span> — a
                  forge house founded in 1787 in the city that defined European edge steel. When post-war trade
                  made German imports impossible, Weyersberg's patents, processes, and brands crossed the
                  Atlantic, and on May 20, 1960, Incolma was born.
                </p>
                <p className="text-ink-700 text-lg leading-[1.65]">
                  Today it is <span className="text-ink-950 font-medium">the world's largest machete
                  factory</span> — drop-forging high-carbon blades by the hundreds of thousands every month, in
                  hundreds of patterns, each hardened and tempered to a 52–58 HRC working edge and shipped to
                  working farms in more than 55 countries. In the tropics, where a machete must work every
                  single day, Incolma isn't a brand choice — it's the standard.
                </p>
                <p className="text-ink-700 text-lg leading-[1.65]">
                  <span className="text-ink-950 font-medium">The Machete Man is Incolma's exclusive Canadian
                  distributor.</span> Every blade here ships factory-direct from Manizales to our Scarborough
                  warehouse — no grey-market imports, no lookalikes. Two centuries of steel, one blade at a time.
                </p>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};

export default HeritageStory;
